import { NotificationChannelEnum } from "@sinch/core/entities/serverEnums";
import { MdsChatBubbleOff, MdsMail, MdsMailOff, MdsNotifications, MdsNotificationsOff, MdsSms } from "@sinch/icons";
import React, { ElementType } from "react";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

export enum NotificationChannel {
  Sms = NotificationChannelEnum.Sms,
  Email = NotificationChannelEnum.Email,
  Push = NotificationChannelEnum.Push,
}

/**
 * Single source of truth for how a message channel looks in the app: label and icons.
 *
 * Use these getters wherever a message channel is shown.
 * Never hardcode its label and icons in a component, change them here.
 * Every value is shown in the "Entity Identity/MessageChannel" story.
 */
export const useMessageChannels = () => {
  const { t } = useTranslation();

  const channels = {
    [NotificationChannel.Sms]: {
      label: t("Messages.channelSms"),
      icon: MdsSms,
      offIcon: MdsChatBubbleOff,
    },
    [NotificationChannel.Email]: {
      label: t("Messages.channelEmail"),
      icon: MdsMail,
      offIcon: MdsMailOff,
    },
    [NotificationChannel.Push]: {
      label: t("Messages.channelPush"),
      icon: MdsNotifications,
      offIcon: MdsNotificationsOff,
    },
  } as Record<NotificationChannel, { label: string; icon: React.ElementType; offIcon: React.ElementType }>;
  const getProp = useEntityProps(channels);

  const getChannelLabel = getProp<string>("label");
  const getChannelIcon = getProp<ElementType>("icon");
  const getChannelOffIcon = getProp<ElementType>("offIcon");
  const getChannelList: () => NotificationChannel[] = () => [
    NotificationChannel.Sms,
    NotificationChannel.Email,
    NotificationChannel.Push,
  ];

  return { getChannelLabel, getChannelIcon, getChannelList, getChannelOffIcon };
};
